import React, { useState } from 'react';
import { Nav, Button, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCurriculum } from '../../context/CurriculumContext';
import {
  Building,
  PersonFill,
  BookFill,
  Calendar,
  House,
  Book,
  ChevronLeft,
  ChevronRight,
  Grid3x3Gap,
  KeyFill,
  CashCoin,
  CpuFill,
  TicketDetailedFill,
  QuestionCircleFill,
  BuildingFill,
  PersonBadgeFill
} from 'react-bootstrap-icons';
import { useSidebar } from '../../context/SidebarContext';

const GlobalSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { matrixHasUnsavedChanges, triggerMatrixSave } = useCurriculum() || {};
  const [pendingPath, setPendingPath] = useState(null);
  const [savingBeforeNav, setSavingBeforeNav] = useState(false);

  const getMenuItems = () => {
    const baseItems = [
      {
        key: 'home',
        title: 'Inicio',
        icon: <House size={18} />,
        path: '/docente/inicio',
        section: 'main'
      }
    ];

    // Vista Maestra
    if (location.pathname.startsWith('/master')) {
      return [
        {
          key: 'master-establishments-dashboard',
          title: 'Costo por Establecimiento',
          icon: <CashCoin size={18} />,
          path: '/master/dashboard/establishments',
          section: 'master'
        },
        {
          key: 'master-ai',
          title: 'Uso de IA',
          icon: <CpuFill size={18} />,
          path: '/master/dashboard/ai',
          section: 'master'
        },
        {
          key: 'master-tickets',
          title: 'Gestión de Tickets',
          icon: <TicketDetailedFill size={18} />,
          path: '/master/tickets',
          section: 'master'
        },
        {
          key: 'master-users',
          title: 'Gestionar Usuarios',
          icon: <KeyFill size={18} />,
          path: '/master/users',
          section: 'master'
        },
        {
          key: 'master-tenants',
          title: 'Establecimientos',
          icon: <BuildingFill size={18} />,
          path: '/master/tenants',
          section: 'master'
        },
        {
          key: 'master-academic-content',
          title: 'Gestionar Marco Académico',
          icon: <BookFill size={18} />,
          path: '/master/academic-content',
          section: 'master'
        }
      ];
    }

    // Si estamos en la vista de administrador, mostrar menú de admin (sin Inicio)
    if (location.pathname.startsWith('/admin')) {
      return [
        {
          key: 'admin-organizations',
          title: 'Gestionar Organizaciones',
          icon: <Building size={18} />,
          path: '/admin/organizations',
          section: 'admin'
        },
        {
          key: 'admin-persons',
          title: 'Gestionar Personas',
          icon: <PersonFill size={18} />,
          path: '/admin/persons',
          section: 'admin'
        },
        {
          key: 'admin-users',
          title: 'Gestionar Usuarios',
          icon: <KeyFill size={18} />,
          path: '/admin/users',
          section: 'admin'
        },
        {
          key: 'admin-academic-content',
          title: 'Gestionar Marco Académico',
          icon: <BookFill size={18} />,
          path: '/admin/academic-content',
          section: 'admin'
        },
        {
          key: 'admin-academic-schedule',
          title: 'Gestionar Horario Académico',
          icon: <Calendar size={18} />,
          path: '/admin/academic-schedule',
          section: 'admin'
        },
        {
          key: 'admin-soporte',
          title: 'Soporte',
          icon: <QuestionCircleFill size={18} />,
          path: '/admin/soporte',
          section: 'admin'
        }
      ];
    }

    // Si estamos en la vista de UTP, mostrar menú de UTP
    if (location.pathname.startsWith('/utp')) {
      return [
        {
          key: 'utp-home',
          title: 'Inicio',
          icon: <House size={18} />,
          path: '/utp/inicio',
          section: 'utp'
        },
        {
          key: 'utp-planning',
          title: 'Planificación',
          icon: <Calendar size={18} />,
          path: '/utp/planning',
          section: 'utp'
        },
        {
          key: 'utp-monitoring',
          title: 'Monitoreo',
          icon: <Grid3x3Gap size={18} />,
          path: '/utp/monitoring',
          section: 'utp'
        },
        {
          key: 'utp-staffing',
          title: 'Dotación Docente',
          icon: <PersonBadgeFill size={18} />,
          path: '/utp/dotacion',
          section: 'utp'
        },
        {
          key: 'utp-soporte',
          title: 'Soporte',
          icon: <QuestionCircleFill size={18} />,
          path: '/utp/soporte',
          section: 'utp'
        }
      ];
    }

    // Vista docente por defecto
    return [
      ...baseItems,
      {
        key: 'teacher-classes',
        title: 'Mis Clases',
        icon: <Book size={18} />,
        path: '/docente/clases',
        section: 'teacher'
      },
      {
        key: 'teacher-planning',
        title: 'Planificación',
        icon: <Calendar size={18} />,
        path: '/docente/planificacion',
        section: 'teacher'
      },
      {
        key: 'teacher-soporte',
        title: 'Soporte',
        icon: <QuestionCircleFill size={18} />,
        path: '/docente/soporte',
        section: 'teacher'
      }
      // {
      //   key: 'teacher-students',
      //   title: 'Estudiantes',
      //   icon: <People size={18} />,
      //   path: '/docente/estudiantes',
      //   section: 'teacher'
      // },
      // {
      //   key: 'teacher-evaluation',
      //   title: 'Evaluación',
      //   icon: <PersonCheck size={18} />,
      //   path: '/docente/evaluacion',
      //   section: 'teacher'
      // }
    ];
  };

  const menuItems = getMenuItems();

  // Guardia de navegación (docs/new_bulk_ia.md §2.1.1): si el docente deja
  // "Planificación" con cambios sin guardar en la matriz OA x Clase, se le
  // pregunta antes de navegar en vez de perder los cambios en silencio.
  const handleNavClick = (path) => {
    if (
      path !== location.pathname &&
      location.pathname.startsWith('/docente/planificacion') &&
      matrixHasUnsavedChanges
    ) {
      setPendingPath(path);
      return;
    }
    navigate(path);
  };

  const handleCancelNav = () => {
    setPendingPath(null);
  };

  const handleNavWithoutSaving = () => {
    const target = pendingPath;
    setPendingPath(null);
    navigate(target);
  };

  const handleSaveAndNav = async () => {
    const target = pendingPath;
    setSavingBeforeNav(true);
    try {
      await triggerMatrixSave();
    } finally {
      setSavingBeforeNav(false);
    }
    setPendingPath(null);
    navigate(target);
  };

  const getSectionTitle = () => {
    if (location.pathname.startsWith('/admin')) return 'Panel de Administración';
    if (location.pathname.startsWith('/utp')) return 'Panel UTP';
    if (location.pathname.startsWith('/master')) return 'Vista Maestra';
    return 'Panel Docente';
  };

  const getSectionSubtitle = () => {
    if (location.pathname.startsWith('/admin')) return 'Gestiona todos los aspectos del sistema';
    if (location.pathname.startsWith('/utp')) return 'Supervisa y coordina el proceso educativo';
    if (location.pathname.startsWith('/master')) return 'Supervisión global de la plataforma';
    return 'Gestiona tu proceso de enseñanza';
  };

  return (
    <div className={`global-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="global-sidebar-header">
        {!isCollapsed && (
          <>
            <h5 className="mb-0">{getSectionTitle()}</h5>
            <p className="text-muted small mb-0"></p>
          </>
        )}
      </div>
      
      {/* Botón toggle en el centro derecho */}
      <div className="sidebar-toggle-container">
        <Button
          variant="outline-light"
          size="sm"
          className="sidebar-toggle-btn"
          onClick={toggleSidebar}
          title={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </Button>
      </div>
      
      <div className="flex-column global-nav">
        {menuItems.map(item => (
          <Nav.Link
            key={item.key}
            onClick={() => handleNavClick(item.path)}
            className={`global-nav-link ${location.pathname === item.path ? 'active' : ''}`}
            title={isCollapsed ? item.title : ''}
          >
            <span className="d-flex align-items-center gap-2">
              {item.icon}
              {!isCollapsed && item.title}
            </span>
          </Nav.Link>
        ))}
      </div>

      {/* Modal de cambios sin guardar al salir de "Planificación" (docs/new_bulk_ia.md §2.1.1) */}
      <Modal show={!!pendingPath} onHide={handleCancelNav} centered dialogClassName="pm-modal">
        <Modal.Header closeButton>
          <Modal.Title>Cambios sin guardar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tienes cambios sin guardar en la matriz de planificación. ¿Qué deseas hacer antes de salir?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="btn-artemis btn-artemis-ghost btn-artemis-icon btn btn-secondary"
            onClick={handleCancelNav}
            disabled={savingBeforeNav}
          >
            Cancelar
          </Button>
          <Button
            variant="btn-artemis btn-artemis-ghost btn-artemis-icon btn btn-secondary"
            onClick={handleNavWithoutSaving}
            disabled={savingBeforeNav}
          >
            Continuar sin guardar
          </Button>
          <Button
            variant="btn-artemis btn-artemis-primary btn-artemis-icon btn btn-secondary"
            onClick={handleSaveAndNav}
            disabled={savingBeforeNav}
          >
            {savingBeforeNav ? 'Guardando...' : 'Guardar y continuar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GlobalSidebar;
